import React from 'react';
import './BulkUploadQueue.css';

const BulkUploadQueue = ({ files, onRemove, onClear, onRetry, onViewTranscript, formatFileSize }) => {
  if (!files || files.length === 0) {
    return null;
  }

  // Debug: Log all files to see their structure
  console.log('BulkUploadQueue files:', files);

  const totalSize = files.reduce((sum, f) => sum + (f.size || 0), 0);
  const completedCount = files.filter(f => f.status === 'completed').length;
  const failedCount = files.filter(f => f.status === 'failed').length;
  const processingCount = files.filter(f => 
    f.status === 'converting' || f.status === 'uploading' || f.status === 'processing'
  ).length;

  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return '⏳';
      case 'converting':
      case 'uploading':
      case 'processing':
        return '🔄';
      case 'completed':
        return '✅';
      case 'failed':
        return '❌';
      case 'cancelled':
        return '⏹️';
      default:
        return '';
    }
  };

  const getStatusText = (status, statusText) => {
    if (statusText && status !== 'completed' && status !== 'failed') {
      return statusText;
    }
    
    switch (status) {
      case 'pending':
        return 'Waiting';
      case 'converting':
        return 'Converting';
      case 'uploading':
        return 'Transcribing';
      case 'processing':
        return 'Processing';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  // Handle view transcript - try multiple ways to get transcriptId
  const handleViewTranscript = (file) => {
    console.log('View transcript clicked for file:', file);
    
    // Try multiple ways to get transcriptId
    let transcriptId = file.transcriptId;
    
    if (!transcriptId && file.transcript) {
      // Check if transcript object has an id
      transcriptId = file.transcript.id;
    }
    
    if (!transcriptId && file.transcriptUrl) {
      // Extract from transcriptUrl
      const urlMatch = file.transcriptUrl.match(/transcript=([^&]+)/);
      if (urlMatch) {
        transcriptId = urlMatch[1];
      }
    }
    
    console.log('Found transcriptId for viewing:', transcriptId);
    
    if (transcriptId) {
      if (onViewTranscript) {
        onViewTranscript(transcriptId, file.transcript);
      } else {
        window.location.href = `/?transcript=${transcriptId}`;
      }
    } else {
      console.error('No transcriptId found for file:', file);
      alert('Transcript ID not available. Please try refreshing the page.');
    }
  };

  // Handle remove with confirmation for active uploads
  const handleRemove = (fileId, status) => {
    if (['converting', 'uploading', 'processing'].includes(status)) {
      if (window.confirm('This file is currently processing. Are you sure you want to cancel?')) {
        onRemove(fileId);
      }
    } else {
      onRemove(fileId);
    }
  };

  return (
    <div className="bulk-upload-queue">
      <div className="queue-header">
        <div className="queue-title">
          <h3>Upload Queue ({files.length} files)</h3>
        </div>
        <div className="queue-stats">
          <span className="stat">
            {completedCount}/{files.length} completed
          </span>
          {failedCount > 0 && (
            <span className="stat failed">
              {failedCount} failed
            </span>
          )}
          {processingCount > 0 && (
            <span className="stat processing">
              {processingCount} active
            </span>
          )}
          <span className="stat">
            {formatSize(totalSize)} total
          </span>
        </div>
        <button
          className="clear-queue-btn"
          onClick={onClear}
          title="Clear all completed and failed"
        >
          Clear
        </button>
      </div>

      <div className="queue-list">
        {files.map((file) => {
          // Debug each file
          console.log('Rendering file:', file.name, 'status:', file.status, 'transcriptId:', file.transcriptId);
          
          const hasTranscriptId = file.transcriptId || 
                                 (file.transcript && file.transcript.id) || 
                                 file.transcriptUrl;
          
          return (
            <div key={file.id} className={`queue-item ${file.status}`}>
              <div className="file-icon">
                {getStatusIcon(file.status)}
              </div>
              <div className="file-info">
                <div className="file-name">{file.name}</div>
                <div className="file-details">
                  <span>{formatSize(file.size)}</span>
                  <span className="separator">•</span>
                  <span>{getStatusText(file.status, file.statusText)}</span>
                  
                  {/* Show progress percentage for active files */}
                  {(file.status === 'converting' || file.status === 'uploading' || file.status === 'processing') && file.progress !== undefined && (
                    <>
                      <span className="separator">•</span>
                      <span>{Math.round(file.progress)}%</span>
                    </>
                  )}
                  
                  {/* Show error for failed files */}
                  {file.status === 'failed' && file.error && (
                    <>
                      <span className="separator">•</span>
                      <span className="error-text">
                        {file.error.length > 50 ? file.error.substring(0, 50) + '...' : file.error}
                      </span>
                    </>
                  )}
                  
                  {/* Show completion time for completed files */}
                  {file.status === 'completed' && file.completedAt && (
                    <>
                      <span className="separator">•</span>
                      <span className="completed-time">
                        {new Date(file.completedAt).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </span>
                    </>
                  )}
                </div>
                
                {/* Progress bar for active uploads */}
                {(file.status === 'converting' || file.status === 'uploading' || file.status === 'processing') && file.progress !== undefined && (
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${file.progress}%` }}
                    />
                  </div>
                )}
              </div>
              <div className="file-actions">
                {/* VIEW BUTTON - Only for completed files */}
                {file.status === 'completed' && (
                  <button
                    className="view-btn"
                    onClick={() => handleViewTranscript(file)}
                    title="View transcript"
                  >
                    View
                  </button>
                )}
                
                {/* RETRY BUTTON - For failed files */}
                {file.status === 'failed' && (
                  <button
                    className="retry-btn"
                    onClick={() => {
                      if (onRetry) {
                        onRetry(file.id);
                      }
                    }}
                    title="Retry upload"
                  >
                    🔄 Retry
                  </button>
                )}
                
                {/* REMOVE/CANCEL BUTTON - For all files */}
                <button
                  className={`remove-btn ${file.status === 'converting' || file.status === 'uploading' || file.status === 'processing' ? 'cancel-btn' : ''}`}
                  onClick={() => handleRemove(file.id, file.status)}
                  title={file.status === 'converting' || file.status === 'uploading' || file.status === 'processing' ? 'Cancel upload' : 'Remove from queue'}
                >
                  {file.status === 'converting' || file.status === 'uploading' || file.status === 'processing' ? '⏹️ Cancel' : '×'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BulkUploadQueue;
